import { useForm, useStore } from '@tanstack/react-form'
import { Button } from '@trellis/roots/button'
import { Checkbox } from '@trellis/roots/checkbox'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@trellis/roots/command'
import { RichTextEditor } from '@trellis/roots/data'
import {
  CurrencyInput,
  DatePicker,
  FormFooter,
  FormSection,
  QuantitySelector,
} from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { Popover, PopoverContent, PopoverTrigger } from '@trellis/roots/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import { Skeleton } from '@trellis/roots/skeleton'
import { TagSelector } from '@trellis/roots/tags'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import {
  APP_CURRENCY,
  CreateBusinessExpenseSchema,
  CreateReimbursementExpenseSchema,
  EXPENSE_CATEGORY_META,
  FEATURE,
  getQuantityStepForUnitType,
  TAX_TYPE_DEFAULTS,
  UNIT_TYPE_DEFAULTS,
  type ClaimableType,
  type CreateBusinessExpense,
  type CreateRecurringExpense,
  type CreateReimbursementExpense,
  type ExpenseCategory,
  type ExpenseResponse,
  type ExpenseType,
  type InvoiceTaxType,
  type RecurrenceFrequency,
  type UpdateRecurringExpense,
} from '@trellis/shared'
import { format, parseISO } from 'date-fns'
import {
  Briefcase,
  Check,
  ChevronsUpDown,
  ClipboardList,
  FileText,
  HandCoins,
  Info,
  Receipt,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { toast } from 'sonner'
import { z } from 'zod'

import { ClientCombobox } from '@/components/client-combobox'
import { TypeCardRadioGroup } from '@/components/type-card-radio-group'
import {
  useCreateTagMutation,
  useInvoicesQuery,
  useLineItemsSummaryQuery,
  useTagsQuery,
} from '@/hooks'
import { useIsFeatureEnabled } from '@/hooks/useFeatureFlags'
import { useTaxDefaults } from '@/hooks/useOrganization'
import { reportInvalidFields } from '@/lib/analytics'
import { formatCurrency } from '@/lib/format'
import { cn } from '@/lib/utils'

import type { ScheduleSectionValue } from '../../invoices/(components)/ScheduleSection'
import { ScheduleSection } from '../../invoices/(components)/ScheduleSection'

export enum ExpenseFormMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  EDIT_RECURRING = 'EDIT_RECURRING',
}

/**
 * ExpenseDate is stored as a full ISO datetime — extracts the yyyy-MM-dd DatePicker
 * needs.
 */
export function expenseDateToDateOnly(isoDateTime: string): string {
  return format(parseISO(isoDateTime), 'yyyy-MM-dd')
}

/**
 * Anchors the picked date at local noon before serialising, so the UTC-converted
 * timestamp still falls on the same calendar day for all but the most extreme (>12h)
 * timezone offsets.
 */
export function dateOnlyToNoonISOString(dateOnly: string): string {
  const noon = parseISO(dateOnly)
  noon.setHours(12, 0, 0, 0)
  return noon.toISOString()
}

/** Defaults pre-populated when editing an existing recurring expense. */
export interface RecurringExpenseDefaults {
  type: 'BUSINESS' | 'REIMBURSEMENT'
  category?: ExpenseCategory
  clientId?: string
  title: string
  totalCost: number
  taxIncluded: boolean
  taxType?: InvoiceTaxType
  taxRate?: number
  claimableAmount: number
  claimableAmountType: 'DOLLARS' | 'PERCENTAGE'
  description?: string
  frequency: RecurrenceFrequency
  interval: number
  daysOfWeek?: number[]
  dayOfMonth?: number
  monthOfYear?: number
  startDate: string
  endDate?: string
  maxOccurrences?: number
}

function defaultScheduleValue(
  enabled: boolean,
  defaults?: RecurringExpenseDefaults,
): ScheduleSectionValue {
  const endConditionType = defaults?.endDate
    ? 'on_date'
    : defaults?.maxOccurrences
      ? 'after_n'
      : 'never'
  return {
    enabled,
    frequency: defaults?.frequency ?? 'MONTHLY',
    interval: defaults?.interval ?? 1,
    daysOfWeek: defaults?.daysOfWeek ?? [],
    dayOfMonth: defaults?.dayOfMonth,
    monthOfYear: defaults?.monthOfYear,
    nextRunAt: defaults?.startDate ? defaults.startDate.substring(0, 10) : '',
    endConditionType,
    maxRuns: defaults?.maxOccurrences ? String(defaults.maxOccurrences) : '',
    endDate: defaults?.endDate ? defaults.endDate.substring(0, 10) : '',
    autoSend: false,
  }
}

const CATEGORY_OPTIONS = (
  Object.entries(EXPENSE_CATEGORY_META) as [
    ExpenseCategory,
    { label: string; code: number },
  ][]
).sort((a, b) => a[1].label.localeCompare(b[1].label))

const EXPENSE_TYPE_OPTIONS = [
  {
    value: 'BUSINESS',
    label: 'Business expense',
    description: 'A deductible expense for your own records — not billed to a client.',
    icon: Briefcase,
  },
  {
    value: 'REIMBURSEMENT',
    label: 'Client reimbursement',
    description: 'An expense incurred on behalf of a client, billed back to them.',
    icon: HandCoins,
  },
] as const satisfies readonly {
  value: ExpenseType
  label: string
  description: string
  icon: typeof Briefcase
}[]

interface LabelWithTooltipProps {
  htmlFor: string
  tooltip: string
  children: ReactNode
}

function LabelWithTooltip({ htmlFor, tooltip, children }: LabelWithTooltipProps) {
  return (
    <div className="flex items-center gap-1.5">
      <Label htmlFor={htmlFor}>{children}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground"
            aria-label="More information"
          >
            <Info className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </div>
  )
}

const UNIT_TYPE_LABELS: Record<string, string> = {
  HOUR: 'Hour',
  DAY: 'Day',
  WEEK: 'Week',
  MONTH: 'Month',
  UNIT: 'Unit',
  FLAT: 'Flat',
}

interface ExpenseFormProps {
  mode?: ExpenseFormMode
  defaultValues?: Partial<ExpenseResponse>
  recurringDefaults?: RecurringExpenseDefaults
  onSubmit: (
    data:
      | CreateBusinessExpense
      | CreateReimbursementExpense
      | CreateRecurringExpense
      | UpdateRecurringExpense,
    billToInvoiceId?: string,
  ) => void
  isSubmitting?: boolean
  readOnly?: boolean
  requireTitle?: boolean
  /** Pre-enables the schedule toggle in create mode (e.g. from ?recurring=true). */
  defaultScheduleEnabled?: boolean
  /**
   * Shows the draft-invoice "Add to invoice" selector for one-off reimbursements — create
   * flow only.
   */
  showInvoiceSelector?: boolean
}

export function ExpenseForm({
  mode = ExpenseFormMode.CREATE,
  defaultValues,
  recurringDefaults,
  onSubmit,
  isSubmitting,
  readOnly,
  requireTitle,
  defaultScheduleEnabled = false,
  showInvoiceSelector = false,
}: ExpenseFormProps) {
  const [scheduleValue, setScheduleValue] = useState<ScheduleSectionValue>(() =>
    defaultScheduleValue(
      mode === ExpenseFormMode.EDIT_RECURRING || defaultScheduleEnabled,
      recurringDefaults,
    ),
  )

  const isRecurring =
    mode === ExpenseFormMode.EDIT_RECURRING ||
    (mode === ExpenseFormMode.CREATE && scheduleValue.enabled)
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)

  const [isLineItemOpen, setIsLineItemOpen] = useState(false)

  const { data: lineItemsResult } = useLineItemsSummaryQuery()
  const lineItems = lineItemsResult?.data ?? []

  const { isEnabled: isTagsEnabled } = useIsFeatureEnabled(FEATURE.TAGS)
  const { data: allTags = [] } = useTagsQuery()
  const createTagMutation = useCreateTagMutation()

  const isEditing =
    mode === ExpenseFormMode.EDIT || mode === ExpenseFormMode.EDIT_RECURRING

  const { defaultTaxType, defaultTaxRate } = useTaxDefaults()

  const form = useForm({
    defaultValues: {
      type: (recurringDefaults?.type ?? defaultValues?.type ?? 'BUSINESS') as ExpenseType,
      expenseDate: defaultValues?.expenseDate ?? new Date().toISOString(),
      totalCost: recurringDefaults?.totalCost ?? defaultValues?.totalCost ?? 0,
      taxIncluded: recurringDefaults?.taxIncluded ?? defaultValues?.taxIncluded ?? false,
      taxType: (recurringDefaults?.taxType ??
        (defaultValues as ExpenseResponse & { taxType?: InvoiceTaxType })?.taxType ??
        (!isEditing ? defaultTaxType : null) ??
        'NONE') as InvoiceTaxType,
      taxRate: Math.round(
        (recurringDefaults?.taxRate ??
          (defaultValues as ExpenseResponse & { taxRate?: number })?.taxRate ??
          (!isEditing && defaultTaxType
            ? (defaultTaxRate ?? TAX_TYPE_DEFAULTS[defaultTaxType])
            : 0)) * 100,
      ),
      claimableAmountType: (recurringDefaults?.claimableAmountType ??
        defaultValues?.claimableAmountType ??
        'DOLLARS') as ClaimableType,
      claimableAmount:
        recurringDefaults?.claimableAmount ?? defaultValues?.claimableAmount ?? 0,
      title: recurringDefaults?.title ?? defaultValues?.title ?? '',
      description: recurringDefaults?.description ?? defaultValues?.description ?? '',
      category: (recurringDefaults?.category ??
        defaultValues?.category ??
        '') as ExpenseCategory,
      lineItemId: defaultValues?.lineItemId ?? '',
      claimed: defaultValues?.claimed ?? false,
      clientId: recurringDefaults?.clientId ?? defaultValues?.clientId ?? '',
      unitType: defaultValues?.unitType ?? 'UNIT',
      quantity: defaultValues?.quantity ?? 1,
      reimbursementAcknowledgement: isEditing,
      tagIds: defaultValues?.tags?.map((tag) => tag.id) ?? [],
    },
    onSubmitInvalid: ({ formApi }) =>
      reportInvalidFields('expense', formApi.state.fieldMeta),
    onSubmit: ({ value }) => {
      if (isRecurring) {
        if (!scheduleValue.nextRunAt) {
          toast.error('Start date is required')
          return
        }
        if (!value.title.trim()) {
          toast.error(
            value.type === 'BUSINESS'
              ? 'Expense name is required'
              : 'Line item title is required',
          )
          return
        }
        const startDate = `${scheduleValue.nextRunAt}T00:00:00.000Z`
        const endDate = scheduleValue.endDate
          ? `${scheduleValue.endDate}T00:00:00.000Z`
          : undefined
        const maxOccurrences =
          scheduleValue.endConditionType === 'after_n' && scheduleValue.maxRuns
            ? parseInt(scheduleValue.maxRuns)
            : undefined

        const sharedSchedule = {
          title: value.title,
          totalCost: value.totalCost,
          taxIncluded: value.taxIncluded,
          taxType: value.taxType,
          taxRate: value.taxRate / 100,
          claimableAmount: value.claimableAmount,
          claimableAmountType: value.claimableAmountType,
          description: value.description || undefined,
          frequency: scheduleValue.frequency,
          interval: scheduleValue.interval,
          daysOfWeek: scheduleValue.daysOfWeek,
          isLastDayOfMonth: false,
          dayOfMonth: scheduleValue.dayOfMonth,
          monthOfYear: scheduleValue.monthOfYear,
          startDate,
          endDate,
          maxOccurrences,
        }

        if (value.type === 'BUSINESS') {
          onSubmit({
            type: 'BUSINESS',
            category: value.category,
            lineItemId: null,
            ...sharedSchedule,
          })
        } else {
          onSubmit({
            type: 'REIMBURSEMENT',
            clientId: value.clientId,
            ...sharedSchedule,
          })
        }
        return
      }

      if (value.type === 'BUSINESS') {
        onSubmit(
          CreateBusinessExpenseSchema.parse({
            type: 'BUSINESS',
            expenseDate: value.expenseDate,
            totalCost: value.totalCost,
            taxIncluded: value.taxIncluded,
            taxType: value.taxType,
            taxRate: value.taxRate / 100,
            claimableAmountType: value.claimableAmountType,
            claimableAmount: value.claimableAmount,
            title: value.title || undefined,
            description: value.description || undefined,
            category: value.category,
            lineItemId: value.lineItemId || undefined,
            claimed: value.claimed,
            tagIds: value.tagIds,
          }),
        )
      } else {
        onSubmit(
          CreateReimbursementExpenseSchema.parse({
            type: 'REIMBURSEMENT',
            expenseDate: value.expenseDate,
            totalCost: value.totalCost,
            taxIncluded: value.taxIncluded,
            taxType: value.taxType,
            taxRate: value.taxRate / 100,
            claimableAmountType: value.claimableAmountType,
            claimableAmount: value.claimableAmount,
            title: value.title || undefined,
            description: value.description || undefined,
            clientId: value.clientId,
            unitType: value.unitType,
            quantity: value.quantity,
            tagIds: value.tagIds,
          }),
          selectedInvoiceId || undefined,
        )
      }
    },
  })

  const expenseType = useStore(form.store, (s) => s.values.type)
  const claimableAmountType = useStore(form.store, (s) => s.values.claimableAmountType)
  const totalCost = useStore(form.store, (s) => s.values.totalCost)
  const claimableAmount = useStore(form.store, (s) => s.values.claimableAmount)
  const hasAcknowledged = useStore(
    form.store,
    (s) => s.values.reimbursementAcknowledgement,
  )
  const currentTaxType = useStore(form.store, (s) => s.values.taxType)
  const clientId = useStore(form.store, (s) => s.values.clientId)

  const isReimbursement = expenseType === 'REIMBURSEMENT'

  const [selectedInvoiceId, setSelectedInvoiceId] = useState('')

  useEffect(() => {
    setSelectedInvoiceId('')
  }, [clientId])

  const invoicesQuery = useInvoicesQuery(
    {
      status: 'DRAFT',
      clientId: clientId || undefined,
      limit: 100,
    },
    { enabled: showInvoiceSelector && isReimbursement && !isRecurring && !!clientId },
  )
  const draftInvoices = invoicesQuery.data?.pages.flatMap((page) => page.data ?? []) ?? []

  useEffect(() => {
    if (isEditing || recurringDefaults?.taxType || defaultValues?.taxType) return
    if (defaultTaxType) {
      form.setFieldValue('taxType', defaultTaxType)
      form.setFieldValue(
        'taxRate',
        Math.round((defaultTaxRate ?? TAX_TYPE_DEFAULTS[defaultTaxType]) * 100),
      )
    }
  }, [defaultTaxType, defaultTaxRate, isEditing, recurringDefaults, defaultValues])

  const computedDollarValue =
    claimableAmountType === 'PERCENTAGE' && totalCost > 0
      ? (claimableAmount / 100) * totalCost
      : null

  function getStatusText(): string {
    if (mode === ExpenseFormMode.EDIT) return 'Changes will update this expense.'
    if (mode === ExpenseFormMode.EDIT_RECURRING)
      return 'Changes apply to future runs only.'
    return 'Nothing saved yet.'
  }

  function getSubmitLabel(): string {
    if (isSubmitting) return 'Saving...'
    if (mode === ExpenseFormMode.EDIT_RECURRING) return 'Save changes'
    return 'Save'
  }

  return (
    <form
      noValidate
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()
        void form.handleSubmit()
      }}
      className="space-y-5"
    >
      <fieldset disabled={readOnly} className="m-0 min-w-0 space-y-5 border-0 p-0">
        {!isEditing && (
          <form.Field
            name="type"
            children={(field) => (
              <TypeCardRadioGroup
                id={field.name}
                label="Expense type"
                options={EXPENSE_TYPE_OPTIONS}
                value={field.state.value}
                onChange={field.handleChange}
              />
            )}
          />
        )}

        <FormSection
          icon={ClipboardList}
          title="Summary"
          description={
            isReimbursement
              ? 'What this reimbursement is for and how it will appear on the invoice.'
              : 'Optional notes about this expense.'
          }
        >
          <form.Field
            name="title"
            validators={
              isReimbursement || requireTitle || isRecurring
                ? {
                    onSubmit: ({ value }) => {
                      if (!value?.trim()) return 'Title is required'
                    },
                  }
                : undefined
            }
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>
                  {isReimbursement ? 'Line item title' : 'Expense name'}
                  {(isReimbursement || isRecurring) && (
                    <span className="text-destructive-text ml-1">*</span>
                  )}
                </Label>
                <Input
                  id={field.name}
                  type="text"
                  value={field.state.value ?? ''}
                  onBlur={field.handleBlur}
                  onChange={(changeEvent) => field.handleChange(changeEvent.target.value)}
                  placeholder={
                    isReimbursement
                      ? 'e.g. Travel expenses'
                      : 'e.g. Software subscription'
                  }
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-destructive-text text-sm">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          />

          <form.Field
            name="description"
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Description</Label>
                <div className={readOnly ? 'pointer-events-none' : undefined}>
                  <RichTextEditor
                    id={field.name}
                    value={field.state.value ?? ''}
                    onChange={field.handleChange}
                    placeholder={
                      isReimbursement
                        ? 'Add notes about this reimbursement'
                        : 'Add notes about this expense'
                    }
                  />
                </div>
              </div>
            )}
          />
        </FormSection>

        <FormSection
          icon={FileText}
          title="Details"
          description={
            isReimbursement
              ? 'Which client this reimbursement is for and when it was incurred.'
              : 'When this expense was incurred and how to categorise it.'
          }
        >
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <form.Field
              name="expenseDate"
              validators={{
                onSubmit: ({ value }) => {
                  if (!value) return 'Expense date is required'
                  return undefined
                },
              }}
              children={(field) => {
                return (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Date</Label>
                    <DatePicker
                      id={field.name}
                      value={
                        field.state.value
                          ? expenseDateToDateOnly(field.state.value)
                          : undefined
                      }
                      onChange={(dateOnly) => {
                        if (!dateOnly) return
                        field.handleChange(dateOnlyToNoonISOString(dateOnly))
                      }}
                    />
                    {field.state.meta.errors.length > 0 ? (
                      <Typography variant="body" color="destructive">
                        {String(field.state.meta.errors[0])}
                      </Typography>
                    ) : null}
                  </div>
                )
              }}
            />

            {isReimbursement ? (
              <form.Field
                name="clientId"
                validators={{
                  onSubmit: CreateReimbursementExpenseSchema.shape.clientId,
                }}
                children={(field) => (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Client</Label>
                    <ClientCombobox
                      id={field.name}
                      value={field.state.value}
                      onChange={(client) => field.handleChange(client.id)}
                    />
                    {field.state.meta.errors.length > 0 ? (
                      <Typography variant="body" color="destructive">
                        {field.state.meta.errors[0]?.message}
                      </Typography>
                    ) : null}
                  </div>
                )}
              />
            ) : (
              <form.Field
                name="category"
                validators={{
                  onSubmit: ({ value, fieldApi }) => {
                    if (fieldApi.form.getFieldValue('type') !== 'BUSINESS')
                      return undefined
                    if (!value) return 'Category is required'
                    return undefined
                  },
                }}
                children={(field) => {
                  const selectedLabel = field.state.value
                    ? EXPENSE_CATEGORY_META[field.state.value as ExpenseCategory]?.label
                    : undefined
                  return (
                    <div className="space-y-2">
                      <Label htmlFor={field.name}>Category</Label>
                      <Popover open={isCategoryOpen} onOpenChange={setIsCategoryOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            id={field.name}
                            variant="outline"
                            role="combobox"
                            aria-expanded={isCategoryOpen}
                            className={cn(
                              'w-full justify-between rounded-lg font-normal',
                              !selectedLabel && 'text-muted-foreground',
                            )}
                          >
                            {selectedLabel ?? 'Select a category'}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-[--radix-popover-trigger-width] p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput
                              className="text-base md:text-sm"
                              placeholder="Search categories…"
                            />
                            <CommandList>
                              <CommandEmpty>No category found.</CommandEmpty>
                              <CommandGroup>
                                {CATEGORY_OPTIONS.map(([key, meta]) => (
                                  <CommandItem
                                    key={key}
                                    value={meta.label}
                                    onSelect={() => {
                                      field.handleChange(key as ExpenseCategory)
                                      setIsCategoryOpen(false)
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-4 w-4',
                                        field.state.value === key
                                          ? 'opacity-100'
                                          : 'opacity-0',
                                      )}
                                    />
                                    {meta.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      {field.state.meta.errors.length > 0 ? (
                        <Typography variant="body" color="destructive">
                          {String(field.state.meta.errors[0])}
                        </Typography>
                      ) : null}
                    </div>
                  )
                }}
              />
            )}
          </div>

          {showInvoiceSelector && isReimbursement && !isRecurring && clientId && (
            <div className="space-y-2">
              <LabelWithTooltip
                htmlFor="billToInvoiceId"
                tooltip="Can add to draft invoices to appear in the invoice as a line item."
              >
                Add to invoice (optional)
              </LabelWithTooltip>
              {invoicesQuery.isLoading ? (
                <Skeleton
                  variant="rect"
                  className="h-9 w-full"
                  role="status"
                  aria-label="Loading invoices"
                />
              ) : draftInvoices.length === 0 ? (
                <Typography variant="body" color="muted">
                  No draft invoices found for this client.
                </Typography>
              ) : (
                <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId}>
                  <SelectTrigger id="billToInvoiceId" aria-label="Select an invoice">
                    <SelectValue placeholder="Select an invoice…" />
                  </SelectTrigger>
                  <SelectContent>
                    {draftInvoices.map((invoice) => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.invoiceNumber} — {formatCurrency(invoice.total)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {isReimbursement ? (
            <div
              className={cn('grid gap-4', isRecurring ? 'grid-cols-1' : 'grid-cols-2')}
            >
              <form.Field
                name="unitType"
                children={(field) => (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Unit type</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(value) => {
                        field.handleChange(value)
                        const minStep = getQuantityStepForUnitType(value)
                        if (form.state.values.quantity < minStep) {
                          form.setFieldValue('quantity', minStep)
                        }
                      }}
                    >
                      <SelectTrigger id={field.name} className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {UNIT_TYPE_DEFAULTS.map((option) => (
                          <SelectItem key={option} value={option}>
                            {UNIT_TYPE_LABELS[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />

              {!isRecurring && (
                <form.Subscribe
                  selector={(state) => state.values.unitType}
                  children={(unitType) => (
                    <form.Field
                      name="quantity"
                      validators={{
                        onSubmit: z
                          .number()
                          .positive({ error: 'Quantity must be greater than 0' }),
                      }}
                      children={(field) => (
                        <div className="space-y-2">
                          <Label htmlFor={field.name}>Quantity</Label>
                          <QuantitySelector
                            id={field.name}
                            value={field.state.value}
                            min={getQuantityStepForUnitType(unitType)}
                            step={getQuantityStepForUnitType(unitType)}
                            onChange={field.handleChange}
                            onBlur={field.handleBlur}
                          />
                          {field.state.meta.errors.length > 0 ? (
                            <Typography variant="body" color="destructive">
                              {field.state.meta.errors[0]?.message}
                            </Typography>
                          ) : null}
                        </div>
                      )}
                    />
                  )}
                />
              )}
            </div>
          ) : (
            <form.Field
              name="lineItemId"
              children={(field) => {
                const selectedLineItem = lineItems.find(
                  (li) => li.id === field.state.value,
                )
                return (
                  <div className="space-y-2">
                    <LabelWithTooltip
                      htmlFor={field.name}
                      tooltip="Which line items are dependent and linked to this expense. Used for reporting."
                    >
                      Associated Line Item (optional)
                    </LabelWithTooltip>
                    <Popover open={isLineItemOpen} onOpenChange={setIsLineItemOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id={field.name}
                          variant="outline"
                          role="combobox"
                          aria-expanded={isLineItemOpen}
                          className={cn(
                            'w-full justify-between rounded-lg font-normal',
                            !selectedLineItem && 'text-muted-foreground',
                          )}
                        >
                          {selectedLineItem?.title ?? 'None'}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-[--radix-popover-trigger-width] p-0"
                        align="start"
                      >
                        <Command>
                          <CommandInput
                            className="text-base md:text-sm"
                            placeholder="Search line items…"
                          />
                          <CommandList>
                            <CommandEmpty>No line item found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__none__"
                                onSelect={() => {
                                  field.handleChange('')
                                  setIsLineItemOpen(false)
                                }}
                              >
                                <Check
                                  className={cn(
                                    'mr-2 h-4 w-4',
                                    !field.state.value ? 'opacity-100' : 'opacity-0',
                                  )}
                                />
                                None
                              </CommandItem>
                              {lineItems.map((lineItem) => (
                                <CommandItem
                                  key={lineItem.id}
                                  value={lineItem.title}
                                  onSelect={() => {
                                    field.handleChange(lineItem.id)
                                    setIsLineItemOpen(false)
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      'mr-2 h-4 w-4',
                                      field.state.value === lineItem.id
                                        ? 'opacity-100'
                                        : 'opacity-0',
                                    )}
                                  />
                                  {lineItem.title}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )
              }}
            />
          )}
        </FormSection>

        <FormSection
          icon={Receipt}
          title="Costs"
          description="Total amount paid and how much is claimable."
        >
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <form.Field
              name="totalCost"
              validators={{
                onSubmit: ({ value }) => {
                  const num = Number(value)
                  if (isNaN(num) || num < 0) return 'Total cost must be 0 or greater'
                  return undefined
                },
              }}
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Total Cost</Label>
                  <CurrencyInput
                    id={field.name}
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    currency={APP_CURRENCY}
                    min={0}
                  />
                  {field.state.meta.errors.length > 0 ? (
                    <Typography variant="body" color="destructive">
                      {String(field.state.meta.errors[0])}
                    </Typography>
                  ) : null}
                </div>
              )}
            />

            <form.Field
              name="taxIncluded"
              children={(field) => (
                <div className="space-y-2">
                  <Label>Tax</Label>
                  <div className="flex items-center gap-2 pt-2">
                    <Checkbox
                      id={field.name}
                      checked={field.state.value}
                      onCheckedChange={(checked) => field.handleChange(checked === true)}
                    />
                    <Label htmlFor={field.name} className="cursor-pointer font-normal">
                      Tax included in total?
                    </Label>
                  </div>
                </div>
              )}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <form.Field
              name="taxType"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Tax Type</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => {
                      const type = value as InvoiceTaxType
                      field.handleChange(type)
                      if (type !== 'CUSTOM') {
                        form.setFieldValue(
                          'taxRate',
                          Math.round(TAX_TYPE_DEFAULTS[type] * 100),
                        )
                      }
                    }}
                  >
                    <SelectTrigger id={field.name}>
                      <SelectValue placeholder="Select tax type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NONE">No Tax</SelectItem>
                      <SelectItem value="GST">GST</SelectItem>
                      <SelectItem value="VAT">VAT</SelectItem>
                      <SelectItem value="HST">HST</SelectItem>
                      <SelectItem value="PST">PST</SelectItem>
                      <SelectItem value="SALES_TAX">Sales Tax</SelectItem>
                      <SelectItem value="CUSTOM">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            />

            <form.Field
              name="taxRate"
              children={(field) => {
                const rateReadOnly =
                  currentTaxType !== 'CUSTOM' &&
                  currentTaxType !== 'NONE' &&
                  currentTaxType !== 'SALES_TAX'
                return (
                  <div className="space-y-2">
                    <Label htmlFor={field.name}>Tax Rate (%)</Label>
                    <Input
                      id={field.name}
                      type="number"
                      min={0}
                      max={100}
                      step="0.01"
                      value={field.state.value}
                      readOnly={rateReadOnly}
                      disabled={currentTaxType === 'NONE'}
                      onBlur={field.handleBlur}
                      onChange={(changeEvent) =>
                        field.handleChange(parseFloat(changeEvent.target.value) || 0)
                      }
                      placeholder="0.00"
                    />
                  </div>
                )
              }}
            />
          </div>

          <div className="space-y-3">
            <Label>Claimable Amount</Label>

            <form.Field
              name="claimableAmountType"
              children={(field) => (
                <div className="flex w-fit gap-1 rounded-lg border p-1">
                  {(['DOLLARS', 'PERCENTAGE'] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => field.handleChange(option)}
                      className={cn(
                        'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        field.state.value === option
                          ? 'bg-foreground text-background'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {option === 'DOLLARS' ? '$ Dollars' : '% Percentage'}
                    </button>
                  ))}
                </div>
              )}
            />

            <form.Field
              name="claimableAmount"
              validators={{
                onSubmit: ({ value, fieldApi }) => {
                  const num = Number(value)
                  if (isNaN(num) || num < 0)
                    return 'Claimable amount must be 0 or greater'
                  const type = fieldApi.form.getFieldValue('claimableAmountType')
                  if (type === 'PERCENTAGE' && (num < 0 || num > 100)) {
                    return 'Percentage must be between 0 and 100'
                  }
                  return undefined
                },
              }}
              children={(field) => (
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    {claimableAmountType === 'DOLLARS' ? (
                      <CurrencyInput
                        id={field.name}
                        value={field.state.value}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                        currency={APP_CURRENCY}
                        min={0}
                        className="w-40"
                      />
                    ) : (
                      <Input
                        id={field.name}
                        type="number"
                        min={0}
                        max={100}
                        step="1"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(changeEvent) =>
                          field.handleChange(parseFloat(changeEvent.target.value) || 0)
                        }
                        placeholder="0"
                        className="w-40"
                      />
                    )}
                    {claimableAmountType === 'PERCENTAGE' &&
                      computedDollarValue !== null && (
                        <span className="text-muted-foreground text-sm">
                          = {formatCurrency(computedDollarValue)}
                        </span>
                      )}
                  </div>
                  {field.state.meta.errors.length > 0 ? (
                    <Typography variant="body" color="destructive">
                      {String(field.state.meta.errors[0])}
                    </Typography>
                  ) : null}
                </div>
              )}
            />
          </div>
        </FormSection>

        {isReimbursement ? (
          <form.Field
            name="reimbursementAcknowledgement"
            validators={{
              onSubmit: ({ value }) =>
                !value ? 'You must acknowledge this before saving' : undefined,
            }}
            children={(field) => (
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id={field.name}
                    checked={field.state.value}
                    onCheckedChange={(checked) => field.handleChange(checked === true)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor={field.name}
                    className="cursor-pointer leading-snug font-normal"
                  >
                    I understand this client reimbursement cannot be claimed as a separate
                    business expense.
                  </Label>
                </div>
                {field.state.meta.errors.length > 0 ? (
                  <Typography variant="body" color="destructive">
                    {String(field.state.meta.errors[0])}
                  </Typography>
                ) : null}
              </div>
            )}
          />
        ) : (
          <form.Field
            name="claimed"
            children={(field) => (
              <div className="flex items-start gap-2">
                <Checkbox
                  id={field.name}
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(checked === true)}
                  className="mt-0.5"
                />
                <Label
                  htmlFor={field.name}
                  className="cursor-pointer leading-snug font-normal"
                >
                  Mark as claimed — this expense has been submitted for tax purposes.
                </Label>
              </div>
            )}
          />
        )}

        {isTagsEnabled && (
          <form.Field
            name="tagIds"
            children={(field) => (
              <TagSelector
                tags={allTags}
                value={field.state.value}
                onChange={field.handleChange}
                onCreateTag={async (name, color) => {
                  await createTagMutation.mutateAsync({ name, color })
                }}
                label="Tags"
              />
            )}
          />
        )}

        <ScheduleSection
          value={scheduleValue}
          onChange={setScheduleValue}
          entityLabel="expense"
          locked={mode === ExpenseFormMode.EDIT_RECURRING}
          hidden={mode === ExpenseFormMode.EDIT}
        />

        {!readOnly && (
          <FormFooter
            statusText={getStatusText()}
            actions={[
              {
                label: getSubmitLabel(),
                type: 'submit',
                disabled:
                  isSubmitting || (!isRecurring && isReimbursement && !hasAcknowledged),
              },
            ]}
          />
        )}
      </fieldset>
    </form>
  )
}
